import { useState, Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Input from "../shared/Input";

export default function PriceAddOnModal({
    isOpen,
    onClose,
    project,
    updateProjectAddons,
    refreshProjectPrice
}) {
    const [fatService, setFatService] = useState(project?.fatService ?? 0);
    const [cfdCarParkService, setCfdCarParkService] = useState(project?.cfdCarParkService ?? 0);
    const [cfdThermalService, setCfdThermalService] = useState(project?.cfdThermalService ?? 0);
    const [services, setServices] = useState(project?.services ?? 0);
    const [freight, setFreight] = useState(project?.freight ?? 0);
    const [dutyAndClearance, setDutyAndClearance] = useState(project?.dutyAndClearance ?? 0);
    const [afterSalesService, setAfterSalesService] = useState(project?.afterSalesService ?? 0);
    const [labTestingService, setLabTestingService] = useState(project?.labTestingService ?? 0);
    const [servicesNotes, setServicesNotes] = useState(project?.servicesNotes ?? "");

    useEffect(() => {
        if (isOpen && project) {
            setFatService(project?.fatService ?? 0);
            setCfdCarParkService(project?.cfdCarParkService ?? 0);
            setCfdThermalService(project?.cfdThermalService ?? 0);
            setServices(project?.services ?? 0);
            setFreight(project?.freight ?? 0);
            setDutyAndClearance(project?.dutyAndClearance ?? 0);
            setAfterSalesService(project?.afterSalesService ?? 0);
            setLabTestingService(project?.labTestingService ?? 0);
            setServicesNotes(project?.servicesNotes ?? "");
        }
    }, [isOpen, project]);

    const handleNonNegativeChange = (setter, value) => {
        let sanitized = value.replace(/[^0-9.]/g, '');

        if (sanitized === '') {
            setter(0);
            return;
        }
        let numValue = parseFloat(sanitized);

        if (isNaN(numValue)) {
            setter(0);
            return;
        }
        if (numValue < 0) {
            setter(0);
            return;
        }
        setter(numValue);
    };

    const handleNonNegativeChangeSimple = (setter, value) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) {
            setter(0);
        } else {
            setter(numValue);
        }
    };

    const handleApply = async () => {
        const sanitizedData = {
            fatService: Math.max(0, parseFloat(fatService) || 0),
            cfdCarParkService: Math.max(0, parseFloat(cfdCarParkService) || 0),
            cfdThermalService: Math.max(0, parseFloat(cfdThermalService) || 0),
            services: Math.max(0, parseFloat(services) || 0),
            freight: Math.max(0, parseFloat(freight) || 0),
            dutyAndClearance: Math.max(0, parseFloat(dutyAndClearance) || 0),
            afterSalesService: Math.max(0, parseFloat(afterSalesService) || 0),
            labTestingService: Math.max(0, parseFloat(labTestingService) || 0),
            servicesNotes,
        };

        await updateProjectAddons(sanitizedData);
        onClose();
    };

    const handleCancel = () => {
        setFatService(project?.fatService ?? 0);
        setCfdCarParkService(project?.cfdCarParkService ?? 0);
        setCfdThermalService(project?.cfdThermalService ?? 0);
        setServices(project?.services ?? 0);
        setFreight(project?.freight ?? 0);
        setDutyAndClearance(project?.dutyAndClearance ?? 0);
        setAfterSalesService(project?.afterSalesService ?? 0);
        setLabTestingService(project?.labTestingService ?? 0);
        setServicesNotes(project?.servicesNotes ?? "");
        onClose();
    };

    const createChangeHandler = (setter) => (e) => {
        const value = e.target.value;
        if (value === '') {
            setter(0);
            return;
        }

        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) {
            setter(0);
        } else {
            setter(numValue);
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleCancel}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
                                <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-6 text-gray-900"
                                    >
                                        Services & Freight
                                    </Dialog.Title>
                                    <button
                                        onClick={handleCancel}
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className="bg-white px-6 py-4 max-h-[70vh] overflow-y-auto">
                                    <div className="mb-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Input
                                                elementtype="input"
                                                label="FAT – Air & Sound Performance"
                                                type="number"
                                                min="0"
                                                placeholder="0 AED"
                                                value={fatService}
                                                onChange={createChangeHandler(setFatService)}
                                                onKeyDown={(e) => {
                                                    // Prevent minus sign
                                                    if (e.key === '-') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                autoFocus
                                            />
                                            <Input
                                                elementtype="input"
                                                label="CFD – Carpark Design & Services"
                                                type="number"
                                                min="0"
                                                placeholder="0 AED"
                                                value={cfdCarParkService}
                                                onChange={createChangeHandler(setCfdCarParkService)}
                                                onKeyDown={(e) => {
                                                    if (e.key === '-') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                            <Input
                                                elementtype="input"
                                                label="CFD – Thermal Analysis Services"
                                                type="number"
                                                min="0"
                                                placeholder="0 AED"
                                                value={cfdThermalService}
                                                onChange={createChangeHandler(setCfdThermalService)}
                                                onKeyDown={(e) => {
                                                    if (e.key === '-') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                            <Input
                                                elementtype="input"
                                                label="Services"
                                                type="number"
                                                min="0"
                                                placeholder="0 AED"
                                                value={services}
                                                onChange={createChangeHandler(setServices)}
                                                onKeyDown={(e) => {
                                                    if (e.key === '-') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Input
                                            elementtype="input"
                                            label="Freight"
                                            type="number"
                                            min="0"
                                            placeholder="0 AED"
                                            value={freight}
                                            onChange={createChangeHandler(setFreight)}
                                            onKeyDown={(e) => {
                                                if (e.key === '-') {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                        <Input
                                            elementtype="input"
                                            label="Duty and Clearance"
                                            type="number"
                                            min="0"
                                            placeholder="0 %"
                                            value={dutyAndClearance}
                                            onChange={createChangeHandler(setDutyAndClearance)}
                                            onKeyDown={(e) => {
                                                if (e.key === '-') {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Input
                                            elementtype="input"
                                            label="After Sales Service"
                                            type="number"
                                            min="0"
                                            placeholder="0 AED"
                                            value={afterSalesService}
                                            onChange={createChangeHandler(setAfterSalesService)}
                                            onKeyDown={(e) => {
                                                if (e.key === '-') {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                        <Input
                                            elementtype="input"
                                            label="Lab Testing Service"
                                            type="number"
                                            min="0"
                                            placeholder="0 AED"
                                            value={labTestingService}
                                            onChange={createChangeHandler(setLabTestingService)}
                                            onKeyDown={(e) => {
                                                if (e.key === '-') {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Services Notes</h4>
                                        <textarea
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                            rows="3"
                                            placeholder="Enter service notes..."
                                            value={servicesNotes}
                                            onChange={(e) => setServicesNotes(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                                    <button
                                        type="button"
                                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                                        onClick={handleApply}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}